const HYPHEN = '-';
const SEPARATORS = {
    pipe: ' | ',
    comma: ', ',
    newline: '\n',
    none: '',
};
const TIME_UNITS = [
    'years',
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
];
const isEmptyInput = (value) => value === null ||
    value === undefined ||
    value === '' ||
    typeof value === 'function';
const resolveEmptyFormat = (state) => state?.emptyFormat === 'empty' || state?.emptyFormat === 'stringify'
    ? state.emptyFormat
    : 'hyphen';
const emptyResult = (state, value) => {
    const mode = resolveEmptyFormat(state);
    if (mode === 'empty')
        return '';
    if (mode === 'stringify') {
        if (isEmptyInput(value))
            return '';
        return String(value);
    }
    return HYPHEN;
};
const isHyphenEmptyToken = (body, state) => body === HYPHEN && resolveEmptyFormat(state) === 'hyphen';
const applyTextCase = (text, textCase) => {
    if (textCase === 'all')
        return text.toUpperCase();
    if (textCase === 'word') {
        return text.replace(/\b\p{L}[\p{L}\p{M}]*/gu, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    }
    return text;
};
const decorate = (body, state) => {
    if (body === '' || isHyphenEmptyToken(body, state))
        return body;
    let result = applyTextCase(body, state?.textCase);
    if (typeof state?.textPrefix === 'string' && state.textPrefix.length > 0) {
        result = `${state.textPrefix}${result}`;
    }
    if (typeof state?.textSuffix === 'string' && state.textSuffix.length > 0) {
        result = `${result}${state.textSuffix}`;
    }
    return result;
};
const parseFiniteNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? { value, isRationalSlash: false } : null;
    }
    if (typeof value === 'bigint') {
        try {
            const n = Number(value);
            return Number.isFinite(n) ? { value: n, isRationalSlash: false } : null;
        }
        catch {
            return null;
        }
    }
    if (typeof value === 'string') {
        const trimmed = value.trim().replace(/[\u2212\u2013\u2014]/g, '-');
        if (!trimmed)
            return null;
        const withoutPercent = trimmed
            .replace(/[\s\u00a0]*[%％][\s\u00a0]*$/u, '')
            .trim();
        const rationalMatch = /^-?\d[\d,]*\s*\/\s*-?\d[\d,]*$/.exec(withoutPercent);
        if (rationalMatch) {
            const [rawNum, rawDen] = withoutPercent.split('/').map(s => s.trim());
            const num = Number(rawNum.replace(/,/g, ''));
            const den = Number(rawDen.replace(/,/g, ''));
            if (Number.isFinite(num) && Number.isFinite(den) && den !== 0) {
                return { value: num / den, isRationalSlash: true };
            }
        }
        const normalized = withoutPercent.replace(/[\s\u00a0]/g, '');
        const lastComma = normalized.lastIndexOf(',');
        const lastDot = normalized.lastIndexOf('.');
        let candidate = normalized;
        if (lastComma >= 0 && lastDot >= 0) {
            candidate =
                lastComma > lastDot
                    ? normalized.replace(/\./g, '').replace(',', '.')
                    : normalized.replace(/,/g, '');
        }
        else if (lastComma >= 0 && lastDot < 0) {
            const commaCount = (normalized.match(/,/g) ?? []).length;
            candidate =
                commaCount === 1
                    ? normalized.replace(',', '.')
                    : normalized.replace(/,/g, '');
        }
        else {
            candidate = normalized.replace(/,/g, '');
        }
        const n = Number(candidate);
        return Number.isFinite(n) ? { value: n, isRationalSlash: false } : null;
    }
    return null;
};
const formatWithLocale = (value, locale, intlOptions) => {
    try {
        return value.toLocaleString(locale, intlOptions);
    }
    catch {
        const { roundingMode: _roundingMode, ...rest } = intlOptions;
        return value.toLocaleString(locale, rest);
    }
};
const formatDecimalDigits = (value, { precision, locale, numberFormat, roundingMode, }) => {
    const useGrouping = numberFormat !== 'raw';
    const numberFormatOptions = roundingMode
        ? {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
            roundingMode,
            useGrouping,
        }
        : {
            maximumFractionDigits: precision,
            useGrouping,
        };
    const absValue = Math.abs(value);
    if (numberFormat === 'abbreviated') {
        let shorthandValue;
        if (absValue >= 1_000_000_000_000) {
            shorthandValue =
                formatWithLocale(absValue / 1_000_000_000_000, locale, numberFormatOptions) + 'T';
        }
        else if (absValue >= 1_000_000_000) {
            shorthandValue =
                formatWithLocale(absValue / 1_000_000_000, locale, numberFormatOptions) + 'B';
        }
        else if (absValue >= 1_000_000) {
            shorthandValue =
                formatWithLocale(absValue / 1_000_000, locale, numberFormatOptions) +
                    'M';
        }
        else if (absValue >= 1_000) {
            shorthandValue =
                formatWithLocale(absValue / 1_000, locale, numberFormatOptions) + 'K';
        }
        else {
            shorthandValue = formatWithLocale(absValue, locale, numberFormatOptions);
        }
        return value < 0 ? `-${shorthandValue}` : shorthandValue;
    }
    return formatWithLocale(value, locale, numberFormatOptions);
};
const normalizeNumberUnit = (unit) => {
    if (unit === undefined || unit === null)
        return 'number';
    if (unit === 'decimal')
        return 'number';
    if (unit === 'percent')
        return 'percentage';
    if (unit === 'number' ||
        unit === 'percentage' ||
        unit === 'currency' ||
        TIME_UNITS.includes(unit)) {
        return unit;
    }
    return 'unknown';
};
const isTimeUnit = (unit) => TIME_UNITS.includes(unit);
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/;
const parseWallClockParts = (value, locale) => {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    const match = DATE_SHAPE.exec(trimmed);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = match[4] !== undefined ? Number(match[4]) : 0;
    const minute = match[5] !== undefined ? Number(match[5]) : 0;
    const second = match[6] !== undefined ? Number(match[6]) : 0;
    if (!Number.isInteger(year) ||
        !Number.isInteger(month) ||
        !Number.isInteger(day) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > daysInMonth(year, month) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59) {
        return null;
    }
    const timeInInput = match[4] !== undefined;
    const isMidnight = hour === 0 && minute === 0 && second === 0;
    const hasTime = timeInInput && !isMidnight;
    const monthIndex = month - 1;
    let monthShort = '';
    let monthLong = '';
    try {
        monthShort = new Intl.DateTimeFormat(locale, {
            month: 'short',
            timeZone: 'UTC',
        }).format(new Date(Date.UTC(year, monthIndex, 1)));
        monthLong = new Intl.DateTimeFormat(locale, {
            month: 'long',
            timeZone: 'UTC',
        }).format(new Date(Date.UTC(year, monthIndex, 1)));
    }
    catch {
        monthShort = String(month);
        monthLong = String(month);
    }
    const pad2 = (n) => String(n).padStart(2, '0');
    return {
        year: String(year).padStart(4, '0'),
        month: String(month),
        day: String(day),
        hour: String(hour),
        minute: String(minute),
        second: String(second),
        monthPad: pad2(month),
        dayPad: pad2(day),
        hourPad: pad2(hour),
        minutePad: pad2(minute),
        secondPad: pad2(second),
        monthShort,
        monthLong,
        hasTime,
    };
};
const isValidDateShapeString = (value) => parseWallClockParts(value, 'en-US') !== null;
const resolveDateFormat = (state) => {
    const format = state?.dateFormat;
    if (format === 'iso')
        return 'iso';
    if (format === 'short')
        return 'short';
    if (format === 'medium')
        return 'medium';
    if (format === 'long')
        return 'long';
    return 'medium';
};
const resolveDateGranularity = (state) => {
    const value = state?.dateGranularity;
    if (value === 'second' ||
        value === 'minute' ||
        value === 'hour' ||
        value === 'day' ||
        value === 'week' ||
        value === 'month' ||
        value === 'year') {
        return value;
    }
    return 'day';
};
const isoWeek = (parts) => {
    const date = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const weekYear = date.getUTCFullYear();
    const yearStart = new Date(Date.UTC(weekYear, 0, 1));
    const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return { week, weekYear };
};
const formatDatePortion = (parts, format) => {
    if (format === 'iso') {
        return `${parts.year}-${parts.monthPad}-${parts.dayPad}`;
    }
    if (format === 'short') {
        return `${parts.month}/${parts.day}/${parts.year.slice(-2)}`;
    }
    if (format === 'long') {
        return `${parts.monthLong} ${parts.day}, ${parts.year}`;
    }
    return `${parts.monthShort} ${parts.day}, ${parts.year}`;
};
const formatTimePortion = (parts, granularity) => {
    if (granularity === 'hour')
        return `${parts.hourPad}:00`;
    if (granularity === 'minute') {
        return `${parts.hourPad}:${parts.minutePad}`;
    }
    return `${parts.hourPad}:${parts.minutePad}:${parts.secondPad}`;
};
const formatDateFromParts = (parts, format, granularity) => {
    if (granularity === 'year')
        return parts.year;
    if (granularity === 'week') {
        const { week, weekYear } = isoWeek(parts);
        if (format === 'iso') {
            return `${weekYear}-W${String(week).padStart(2, '0')}`;
        }
        return `Week ${week}, ${weekYear}`;
    }
    if (granularity === 'month') {
        if (format === 'iso')
            return `${parts.year}-${parts.monthPad}`;
        if (format === 'short')
            return `${parts.month}/${parts.year.slice(-2)}`;
        if (format === 'long')
            return `${parts.monthLong} ${parts.year}`;
        return `${parts.monthShort} ${parts.year}`;
    }
    const date = formatDatePortion(parts, format);
    if (granularity === 'day')
        return date;
    const time = formatTimePortion(parts, granularity);
    if (format === 'iso') {
        return granularity === 'hour'
            ? `${date}T${parts.hourPad}`
            : `${date}T${time}`;
    }
    return `${date}, ${time}`;
};
const partsToMs = (parts) => Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
const nowWallClockMs = () => {
    const now = new Date();
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
};
const differenceInHoursSigned = (dateMs, nowMs) => Math.trunc((dateMs - nowMs) / (1000 * 60 * 60));
const differenceInDaysSigned = (dateMs, nowMs) => {
    const day = 1000 * 60 * 60 * 24;
    const startDate = Math.floor(dateMs / day) * day;
    const startNow = Math.floor(nowMs / day) * day;
    return Math.trunc((startDate - startNow) / day);
};
const differenceInWeeksSigned = (dateMs, nowMs) => Math.trunc(differenceInDaysSigned(dateMs, nowMs) / 7);
const differenceInMonthsSigned = (dateMs, nowMs) => {
    const date = new Date(dateMs);
    const now = new Date(nowMs);
    let months = (date.getUTCFullYear() - now.getUTCFullYear()) * 12 +
        (date.getUTCMonth() - now.getUTCMonth());
    if (months === 0)
        return 0;
    const candidateMonthIndex = now.getUTCMonth() + months;
    const candidateYear = now.getUTCFullYear() + Math.floor(candidateMonthIndex / 12);
    const candidateMonth = ((candidateMonthIndex % 12) + 12) % 12;
    const candidateDay = Math.min(now.getUTCDate(), daysInMonth(candidateYear, candidateMonth + 1));
    const candidate = Date.UTC(candidateYear, candidateMonth, candidateDay, now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
    if (months > 0 && candidate > dateMs)
        months -= 1;
    if (months < 0 && candidate < dateMs)
        months += 1;
    return months;
};
const relativeSuffix = (absCount, unitLabel, metric) => {
    const plural = absCount === 1 ? unitLabel.replace(/s$/, '') : unitLabel;
    const unit = unitLabel === 'days' ||
        unitLabel === 'hours' ||
        unitLabel === 'weeks' ||
        unitLabel === 'months'
        ? absCount === 1
            ? unitLabel.slice(0, -1)
            : unitLabel
        : plural;
    if (metric > 0)
        return `${unit} until`;
    return `${unit} ago`;
};
const resolveRelativeUnit = (state) => {
    const unit = state?.dateRelativeUnit;
    if (unit === 'hours' ||
        unit === 'days' ||
        unit === 'weeks' ||
        unit === 'months' ||
        unit === 'adaptive' ||
        unit === 'since' ||
        unit === 'until') {
        return unit;
    }
    return 'days';
};
const resolveSeparator = (state) => {
    const preset = state?.asTextSeparator;
    if (preset === 'pipe' ||
        preset === 'comma' ||
        preset === 'newline' ||
        preset === 'none') {
        return preset;
    }
    return 'pipe';
};
const empty = (state = {}) => (value) => emptyResult(state, value);
const text = (state = {}) => (value) => {
    if (isEmptyInput(value))
        return emptyResult(state, value);
    if (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint') {
        return decorate(String(value), state);
    }
    return emptyResult(state, value);
};
const booleanFmt = (state = {}) => (value) => {
    if (typeof value === 'boolean')
        return String(value);
    return emptyResult(state, value);
};
const id = (state = {}) => text(state);
const numberParts = (state = {}) => (value) => {
    const prefix = typeof state.textPrefix === 'string' ? state.textPrefix : '';
    const suffix = typeof state.textSuffix === 'string' ? state.textSuffix : '';
    const withAffixes = (body) => ({
        prefix,
        value: body,
        suffix,
    });
    if (isEmptyInput(value))
        return withAffixes(emptyResult(state, value));
    const unit = normalizeNumberUnit(state.numberUnit);
    if (unit === 'unknown') {
        throw new Error(`Invalid numberUnit: ${String(state.numberUnit)}`);
    }
    const parsed = parseFiniteNumber(value);
    if (parsed === null)
        return withAffixes(emptyResult(state, value));
    const isPercentage = unit === 'percentage';
    const isCurrency = unit === 'currency';
    const precision = state.numberPrecision ?? (isPercentage ? 0 : unit === 'number' ? 2 : 2);
    const locale = typeof state.numberLocale === 'string' && state.numberLocale.length > 0
        ? state.numberLocale
        : 'en-US';
    const roundingMode = state.numberRoundingMode;
    const scaled = isPercentage && parsed.isRationalSlash ? parsed.value * 100 : parsed.value;
    if (isCurrency) {
        const currency = typeof state.numberCurrency === 'string' &&
            state.numberCurrency.length > 0
            ? state.numberCurrency
            : 'USD';
        const useGrouping = state.numberFormat !== 'raw';
        const currencyOptions = roundingMode
            ? {
                style: 'currency',
                currency,
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
                roundingMode,
                useGrouping,
            }
            : {
                style: 'currency',
                currency,
                maximumFractionDigits: precision,
                useGrouping,
            };
        try {
            return withAffixes(formatWithLocale(scaled, locale, currencyOptions));
        }
        catch {
            return withAffixes(emptyResult(state, value));
        }
    }
    if (isTimeUnit(unit)) {
        const body = formatDecimalDigits(scaled, {
            precision,
            locale,
            numberFormat: state.numberFormat === 'raw' ? 'raw' : 'standard',
            roundingMode,
        });
        return withAffixes(`${body} ${unit}`);
    }
    const body = formatDecimalDigits(scaled, {
        precision,
        locale,
        numberFormat: isPercentage && state.numberFormat === 'abbreviated'
            ? 'standard'
            : state.numberFormat,
        roundingMode,
    });
    return withAffixes(isPercentage ? `${body}%` : body);
};
const numberFmt = (state = {}) => (value) => numberParts(state)(value).value;
const integerParts = (state = {}) => numberParts({
    ...state,
    numberPrecision: state.numberPrecision ?? 0,
});
const integer = (state = {}) => (value) => integerParts(state)(value).value;
const floatParts = numberParts;
const float = (state = {}) => numberFmt(state);
const dateParts = (state = {}) => (value) => {
    if (value instanceof Date)
        return null;
    if (typeof value === 'number')
        return null;
    if (isEmptyInput(value))
        return null;
    const locale = typeof state.numberLocale === 'string' && state.numberLocale.length > 0
        ? state.numberLocale
        : 'en-US';
    return parseWallClockParts(value, locale);
};
const date = (state = {}) => (value) => {
    if (value instanceof Date || typeof value === 'number') {
        return emptyResult(state, value);
    }
    if (isEmptyInput(value))
        return emptyResult(state, value);
    const parts = dateParts(state)(value);
    if (!parts)
        return emptyResult(state, value);
    return formatDateFromParts(parts, resolveDateFormat(state), resolveDateGranularity(state));
};
const relativeDateParts = (state = {}) => (value) => {
    if (value instanceof Date || typeof value === 'number') {
        return null;
    }
    if (isEmptyInput(value))
        return null;
    const parts = dateParts(state)(value);
    if (!parts)
        return null;
    const dateMs = partsToMs(parts);
    const nowMs = nowWallClockMs();
    const unit = resolveRelativeUnit(state);
    const compute = (metric, unitLabel) => {
        const normalizedMetric = Object.is(metric, -0) ? 0 : metric;
        const abs = Math.abs(normalizedMetric);
        return {
            count: String(abs),
            metric: normalizedMetric,
            suffix: relativeSuffix(abs, unitLabel, normalizedMetric),
        };
    };
    if (unit === 'hours') {
        return compute(differenceInHoursSigned(dateMs, nowMs), 'hours');
    }
    if (unit === 'weeks') {
        return compute(differenceInWeeksSigned(dateMs, nowMs), 'weeks');
    }
    if (unit === 'months') {
        return compute(differenceInMonthsSigned(dateMs, nowMs), 'months');
    }
    if (unit === 'adaptive') {
        const months = differenceInMonthsSigned(dateMs, nowMs);
        if (Math.abs(months) >= 1)
            return compute(months, 'months');
        const weeks = differenceInWeeksSigned(dateMs, nowMs);
        if (Math.abs(weeks) >= 1)
            return compute(weeks, 'weeks');
        const days = differenceInDaysSigned(dateMs, nowMs);
        if (Math.abs(days) >= 1)
            return compute(days, 'days');
        return compute(differenceInHoursSigned(dateMs, nowMs), 'hours');
    }
    return compute(differenceInDaysSigned(dateMs, nowMs), 'days');
};
const relativeDate = (state = {}) => (value) => {
    const parts = relativeDateParts(state)(value);
    if (!parts) {
        return emptyResult(state, value);
    }
    return [parts.count, parts.suffix].filter(Boolean).join(' ');
};
const join = (state = {}) => (values) => {
    const list = Array.isArray(values) ? values : [];
    const sep = SEPARATORS[resolveSeparator(state)];
    return list
        .map(v => (v === null || v === undefined ? '' : String(v)))
        .join(sep);
};
const format = (state = {}) => (value) => {
    if (isEmptyInput(value))
        return emptyResult(state, value);
    if (typeof value === 'boolean') {
        return decorate(booleanFmt(state)(value), state);
    }
    if (typeof value === 'bigint')
        return decorate(numberFmt(state)(value), state);
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return decorate(emptyResult(state, value), state);
        }
        return decorate(numberFmt(state)(value), state);
    }
    if (typeof value === 'string') {
        if (isValidDateShapeString(value)) {
            return decorate(date(state)(value), state);
        }
        return text(state)(value);
    }
    return decorate(emptyResult(state, value), state);
};
export const Format = {
    empty,
    text,
    boolean: booleanFmt,
    id,
    integer,
    integerParts,
    float,
    floatParts,
    number: numberFmt,
    numberParts,
    date,
    dateParts,
    relativeDate,
    relativeDateParts,
    join,
    format,
};
//# sourceMappingURL=Format.js.map